import React, { useEffect, useMemo, useState } from 'react';
import { SchedulingService, type AvailabilityDay } from '../../services/schedulingService';
import { seekerDataService } from '../../services/seekerDataService';
import { trackCapiEvent } from '../../utils/trackCapiEvent';
import type { Session, TimeSlot } from '../../types';
import { formatSessionTimeInSeekerTimezone, formatTimeForDisplay, getBrowserTimezone, formatUtcInTimezone, getTimezoneAbbreviation, formatTimeInTimezone } from '../../utils/dateUtils';

type RescheduleModalProps = {
  session: Session;
  onClose: () => void;
  onRescheduleComplete: () => void;
};

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Convert UTC time to seeker's local timezone for display
 */
const convertUtcToSeekerTime = (
  utcTime: string,
  date: string,
  seekerTimezone: string | null
): string => {
  if (!seekerTimezone) {
    return utcTime;
  }

  try {
    // Create full UTC timestamp from date and time
    const utcTimestamp = `${date}T${utcTime}:00Z`;
    // Format in seeker's timezone as HH:mm
    const localTime = formatUtcInTimezone(utcTimestamp, seekerTimezone, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    return localTime;
  } catch (error) {
    console.error('Error converting UTC to seeker time:', error);
    return utcTime;
  }
};

/**
 * Format timezone name for display (e.g., "America/New_York" -> "Eastern Time")
 */
const formatTimezoneForDisplay = (timezone: string | null): string => {
  if (!timezone) return 'UTC';

  try {
    // Get the timezone abbreviation
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortGeneric'
    });
    const parts = formatter.formatToParts(now);
    const tzPart = parts.find(part => part.type === 'timeZoneName');

    if (tzPart) {
      return tzPart.value;
    }

    // Fallback: return the timezone as-is but replace underscores with spaces
    return timezone.split('/').pop()?.replace(/_/g, ' ') || timezone;
  } catch (error) {
    console.error('Error formatting timezone:', error);
    return timezone.split('/').pop()?.replace(/_/g, ' ') || timezone;
  }
};

const canFitSession = (
  timeSlots: Array<{ time: string; available: boolean }>,
  startTime: string,
  durationMinutes: number
): boolean => {
  // Find the index of the start time
  const startIndex = timeSlots.findIndex(slot => slot.time === startTime);
  if (startIndex === -1 || !timeSlots[startIndex].available) {
    return false;
  }

  // Calculate how many 15-minute slots we need (assuming slots are 15 minutes apart)
  const slotsNeeded = Math.ceil(durationMinutes / 15);

  // Check if we have enough consecutive available slots
  for (let i = 0; i < slotsNeeded; i++) {
    const slotIndex = startIndex + i;
    if (slotIndex >= timeSlots.length || !timeSlots[slotIndex].available) {
      return false;
    }
  }

  return true;
};

export const RescheduleModal: React.FC<RescheduleModalProps> = ({ session, onClose, onRescheduleComplete }) => {
  const [availability, setAvailability] = useState<AvailabilityDay[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [selectedStartUtc, setSelectedStartUtc] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [error, setError] = useState<string>('');
  const [seekerTimezone, setSeekerTimezone] = useState<string | null>(null);
  const [seekerPostcode, setSeekerPostcode] = useState<string | null>(null);
  const [guideTimezone, setGuideTimezone] = useState<string | null>(null);

  // Load seeker timezone and postcode on mount
  useEffect(() => {
    const loadSeekerInfo = async () => {
      const [tz, postcode] = await Promise.all([
        seekerDataService.getSeekerTimezone(),
        seekerDataService.getSeekerPostcode(),
      ]);
      setSeekerTimezone(tz || getBrowserTimezone());
      setSeekerPostcode(postcode);
    };
    loadSeekerInfo();
  }, []);

  useEffect(() => {
    if (!session?.guide_id) return;
    setIsLoading(true);

    const fetchAvailability = async () => {
      try {
        const env = (window as any).__ENV__;
        const baseUrl = env?.SUPABASE_URL;

        if (baseUrl) {
          const today = new Date();
          const startDate = today.toISOString().split('T')[0];
          const endDate = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

          const response = await fetch(
            `${baseUrl}/functions/v1/guide-availability?guide_id=${session.guide_id}&start_date=${startDate}&end_date=${endDate}`
          );

          if (response.ok) {
            const data = await response.json();
            // Transform to match expected format
            const transformedAvailability = data.availability.map((day: any) => ({
              ...day,
              dayOfWeek: new Date(day.date).toLocaleDateString('en-US', { weekday: 'long' }),
            }));
            setAvailability(transformedAvailability);
            setGuideTimezone(data.guide_timezone || null);
            return;
          }
        }

        // Fallback to old method
        const data = await SchedulingService.getGuideAvailability(session.guide_id);
        setAvailability(data);
      } catch (loadError) {
        console.error('Error loading availability:', loadError);
        setError('Failed to load availability. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAvailability();
  }, [session?.guide_id]);

  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
    setSelectedTime('');
    setSelectedStartUtc('');
    setError('');
  };

  const handleTimeSelect = (time: string, slotStartUtc?: string) => {
    setSelectedTime(time);
    setSelectedStartUtc(slotStartUtc || '');
    setError('');
  };

  const handleReschedule = async () => {
    if (!selectedDate || !selectedTime) {
      setError('Please select both a date and time.');
      return;
    }

    setIsRescheduling(true);
    setError('');

    try {
      const supabase = (await import('../../lib/supabase')).getSupabaseClient();

      const capiEventId = session.session_type === 'consultation'
        ? trackCapiEvent('Schedule')
        : undefined;

      const { data, error } = await supabase.functions.invoke('session-reschedule', {
        body: {
          session_id: session.id,
          new_date: selectedDate,
          new_time: `${selectedTime}:00`,
          start_utc: selectedStartUtc || undefined,
          seeker_timezone: seekerTimezone || undefined,
          capi_event_id: capiEventId,
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to reschedule session');
      }

      console.log('Session rescheduled:', data);
      onRescheduleComplete();
    } catch (rescheduleError) {
      console.error('Reschedule failed:', rescheduleError);
      setError(rescheduleError instanceof Error ? rescheduleError.message : 'Failed to reschedule session');
    } finally {
      setIsRescheduling(false);
    }
  };

  const formatSessionDateTime = (session: Session) => {
    const tz = seekerTimezone || getBrowserTimezone();

    if (session.start_time && tz) {
      const timeStr = formatTimeInTimezone(session.start_time, tz);
      const tzAbbrev = getTimezoneAbbreviation(session.start_time, tz);
      const timePart = tzAbbrev ? `${timeStr} ${tzAbbrev}` : timeStr;

      const dateStr = formatUtcInTimezone(session.start_time, tz, {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      });
      return `${dateStr} at ${timePart}`;
    }

    // Fallback for legacy sessions
    const { formattedDate, formattedTime } = formatSessionTimeInSeekerTimezone(
      session.start_time,
      seekerTimezone,
      session.session_date,
      session.session_time
    );
    try {
      const dateObj = new Date(session.session_date + 'T00:00:00');
      const dateStr = dateObj.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      });
      return `${dateStr} at ${formattedTime}`;
    } catch {
      return `${formattedDate} at ${formattedTime}`;
    }
  };

  const normalizedToday = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  }, []);

  const [viewingMonth, setViewingMonth] = useState(() => new Date(normalizedToday.getFullYear(), normalizedToday.getMonth(), 1));

  const canGoBack = viewingMonth.getFullYear() > normalizedToday.getFullYear() ||
    (viewingMonth.getFullYear() === normalizedToday.getFullYear() && viewingMonth.getMonth() > normalizedToday.getMonth());

  const handlePrevMonth = () => {
    if (canGoBack) {
      setViewingMonth(new Date(viewingMonth.getFullYear(), viewingMonth.getMonth() - 1, 1));
    }
  };

  const handleNextMonth = () => {
    setViewingMonth(new Date(viewingMonth.getFullYear(), viewingMonth.getMonth() + 1, 1));
  };

  const sessionDuration = useMemo(() => {
    // Use the session's actual duration
    return session.duration_minutes || 60;
  }, [session.duration_minutes]);

  const formatDateKey = (dateObj: Date) => {
    const year = dateObj.getFullYear();
    const month = `${dateObj.getMonth() + 1}`.padStart(2, '0');
    const day = `${dateObj.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const startOfCalendar = useMemo(() => {
    const firstDay = new Date(viewingMonth.getFullYear(), viewingMonth.getMonth(), 1);
    const startDate = new Date(firstDay);
    startDate.setDate(firstDay.getDate() - firstDay.getDay());
    return startDate;
  }, [viewingMonth]);

  const calendarDays = useMemo(() => {
    const days: Array<{
      dateKey: string;
      label: number;
      isPast: boolean;
      hasSlots: boolean;
      isSelected: boolean;
      isCurrentMonth: boolean;
      isOriginal: boolean;
    }> = [];

    for (let i = 0; i < 42; i += 1) {
      const currentDate = new Date(startOfCalendar);
      currentDate.setDate(startOfCalendar.getDate() + i);
      const dateKey = formatDateKey(currentDate);
      const isCurrentMonth = currentDate.getMonth() === viewingMonth.getMonth() && currentDate.getFullYear() === viewingMonth.getFullYear();
      const isPast = currentDate < normalizedToday && dateKey !== formatDateKey(normalizedToday);
      const dayAvailability = availability.find((day) => day.date === dateKey);
      // Check if there are any slots that can fit the full session duration
      const hasSlots = dayAvailability?.timeSlots?.some((slot) =>
        canFitSession(dayAvailability.timeSlots, slot.time, sessionDuration)
      ) ?? false;
      const isSelected = selectedDate === dateKey;
      const isOriginal = session.session_date === dateKey;

      days.push({
        dateKey,
        label: currentDate.getDate(),
        isPast,
        hasSlots,
        isSelected,
        isCurrentMonth,
        isOriginal,
      });
    }

    return days;
  }, [availability, normalizedToday, selectedDate, session.session_date, startOfCalendar, sessionDuration, viewingMonth]);

  const selectedDaySlots = useMemo(() => {
    if (!selectedDate) return [];
    const daySlots = availability.find((day) => day.date === selectedDate)?.timeSlots ?? [];

    // Filter slots to only show those that can fit the full session duration
    return daySlots.map((slot) => ({
      ...slot,
      available: slot.available && canFitSession(daySlots, slot.time, sessionDuration),
    }));
  }, [availability, selectedDate, sessionDuration]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#2C2731]/80 px-3 py-6">
      <div className="w-full max-w-[520px]">
        <div className="flex max-h-[90vh] flex-col overflow-hidden rounded-3xl border border-[#2C2731] bg-[#FFFCEF] shadow-[0_24px_60px_-32px_rgba(15,8,27,0.45)]">
          <header className="flex h-16 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-[#FFFCEF]/30 bg-transparent text-xl text-[#FFFCEF] transition hover:bg-[#FFFCEF]/10"
              onClick={onClose}
              aria-label="Close"
            >
              {'\u2190'}
            </button>
            <div className="flex flex-1 justify-center">
              <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
            </div>
            <div className="w-10" aria-hidden />
          </header>

          <main className="flex flex-1 flex-col gap-6 overflow-y-auto px-6 pb-6 pt-6">
            <div className="rounded-[32px] border border-[#304031]/20 bg-[#FFF8E1]/90 p-6 shadow-[0_32px_60px_-35px_rgba(32,77,60,0.45)]">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#214D3C]/80">Reschedule request</p>
              <h1 className="mt-2 font-recoleta text-[30px] font-normal leading-tight text-[#304031]">
                Pick a new time that works
              </h1>
              <p className="mt-3 text-sm text-[#214D3C]">
                Choose a new date and time. We’ll confirm with your guide instantly.
              </p>
            </div>

            <div className="rounded-2xl border border-[#304031]/15 bg-white/90 p-5 shadow-[0_30px_60px_-40px_rgba(15,8,27,0.45)]">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#214D3C]/80">Current session</p>
              <p className="mt-2 text-sm font-semibold text-[#304031]">
                {formatSessionDateTime(session)}
              </p>
              <p className="text-sm text-[#214D3C]">With {session.guide_name || 'your guide'}</p>
              <p className="mt-1 text-sm text-[#214D3C]">{sessionDuration} minutes</p>
            </div>

            {error && (
              <div className="rounded-2xl border border-[#B24E4E]/30 bg-[#FCEDEA] px-4 py-3 text-sm font-medium text-[#B24E4E]">
                {error}
              </div>
            )}

            {isLoading ? (
              <div className="rounded-2xl border border-[#304031]/15 bg-white/70 p-5">
                <div className="h-4 w-2/3 animate-pulse rounded-full bg-[#E8DFBD]" />
                <div className="mt-4 h-32 animate-pulse rounded-2xl bg-[#E8DFBD]" />
              </div>
            ) : availability.length > 0 ? (
              <>
                <div className="rounded-2xl border border-[#304031]/15 bg-white/85 p-5">
                  <div className="flex items-center justify-between text-[#304031]">
                    <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[#214D3C]/80">Select a date</span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handlePrevMonth}
                        disabled={!canGoBack}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#304031]/20 bg-white text-sm text-[#304031] transition hover:border-[#304031] disabled:cursor-not-allowed disabled:opacity-30"
                        aria-label="Previous month"
                      >
                        {'\u2190'}
                      </button>
                      <span className="min-w-[140px] text-center text-sm font-medium text-[#214D3C]">
                        {viewingMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </span>
                      <button
                        type="button"
                        onClick={handleNextMonth}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#304031]/20 bg-white text-sm text-[#304031] transition hover:border-[#304031]"
                        aria-label="Next month"
                      >
                        {'\u2192'}
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-7 gap-2 text-center text-xs font-semibold text-[#214D3C]/70">
                    {DAY_LABELS.map((label) => (
                      <div key={label}>{label}</div>
                    ))}
                  </div>
                  <div className="mt-2 grid grid-cols-7 gap-2">
                    {calendarDays.map((day) => (
                      <button
                        key={day.dateKey}
                        onClick={() => (!day.isPast && day.hasSlots ? handleDateSelect(day.dateKey) : null)}
                        disabled={day.isPast || !day.hasSlots}
                        className={[
                          'h-12 rounded-2xl border text-sm font-semibold transition disabled:cursor-not-allowed',
                          day.isSelected
                            ? 'border-[#304031] bg-[#304031] text-[#FFFCEF]'
                            : day.hasSlots && day.isCurrentMonth && !day.isPast
                            ? 'border-[#304031]/20 bg-[#FFFCEF] text-[#304031] hover:border-[#304031]'
                            : 'border-transparent bg-transparent text-[#C7C1AD]',
                        ].join(' ')}
                      >
                        {day.label}
                        {day.isOriginal && !day.isSelected && (
                          <span className="block text-[9px] font-semibold text-[#7A7260]">current</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {selectedDate && (
                <div className="rounded-2xl border border-[#304031]/15 bg-[#FFFCEF] p-5">
                    <div className="mb-2">
                      <p className="text-base font-semibold uppercase tracking-[0.12em] text-[#214D3C]/80">Available times</p>
                      {seekerTimezone && (
                        <p className="mt-1 text-base text-[#214D3C]/70">
                          Showing times in {formatTimezoneForDisplay(seekerTimezone)} timezone{seekerPostcode ? `, based on zip code ${seekerPostcode}` : ''}
                        </p>
                      )}
                    </div>
                    {selectedDaySlots.length ? (
                      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                        {selectedDaySlots.map((slot) => {
                          // Convert UTC time to seeker's local time for display
                          const seekerLocalTime = convertUtcToSeekerTime(slot.time, selectedDate, seekerTimezone);
                          return (
                            <button
                              key={slot.time}
                              onClick={() => (slot.available ? handleTimeSelect(slot.time, (slot as any).start_utc) : null)}
                              disabled={!slot.available}
                              className={[
                                'rounded-full border px-4 py-3 text-base font-semibold transition',
                                selectedTime === slot.time && slot.available
                                  ? 'border-[#304031] bg-[#304031] text-[#FFFCEF]'
                                  : slot.available
                                  ? 'border-[#304031]/20 bg-white text-[#304031] hover:border-[#304031]'
                                  : 'border-[#C7C1AD]/30 bg-[#F5F5F5] text-[#C7C1AD] cursor-not-allowed line-through',
                               ].join(' ')}
                            >
                              {formatTimeForDisplay(seekerLocalTime)}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="mt-4 text-sm text-[#214D3C]">No open times for this date.</p>
                    )}
                  </div>
                )}
              </>
            ) : (
            <p className="rounded-2xl border border-[#304031]/15 bg-white/80 px-5 py-4 text-sm text-[#214D3C]">
                No reschedule options are currently available.
              </p>
            )}
          </main>

          <footer className="flex items-center gap-3 border-t border-[#FFFCEF]/25 bg-gradient-to-t from-[#FFFCEF] via-[#FFFCEF] to-[#FFFCEF]/0 px-6 pb-6 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="group inline-flex w-full items-center justify-between gap-3 rounded-full border border-[#304031]/40 bg-[#FFFCEF] px-6 py-4 text-lg font-medium text-[#304031] transition hover:-translate-y-0.5 hover:border-[#304031]"
            >
              <span className="w-5 text-xl leading-none">{'\u2190'}</span>
              <span>Cancel</span>
              <span className="invisible w-5 text-xl leading-none">{'\u2192'}</span>
            </button>
            <button
              type="button"
              onClick={handleReschedule}
              disabled={!selectedDate || !selectedTime || isRescheduling}
              className="group inline-flex w-full items-center justify-between gap-3 rounded-full border border-[#304031] bg-[#304031] px-6 py-4 text-lg font-semibold text-[#FFFCEF] transition hover:-translate-y-0.5 disabled:pointer-events-none disabled:opacity-50"
            >
              <span className="invisible w-5 text-xl leading-none">{'\u2190'}</span>
              <span>{isRescheduling ? 'Rescheduling…' : 'Reschedule'}</span>
              <span className="w-5 text-xl leading-none">{'\u2192'}</span>
            </button>
          </footer>
        </div>
      </div>
    </div>
  );
};
